import React from 'react';
import { 
  Box, 
  TextField, 
  InputAdornment,
  Grid,
  MenuItem,
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';

/**
 * Reusable FilterBar Component
 * @param {string} searchValue - Search input value
 * @param {Function} onSearchChange - Search change handler
 * @param {string} searchPlaceholder - Search placeholder text
 * @param {Array} filters - Array of filter configs { label, value, onChange, options, type }
 */

interface FilterBarProps {
  searchValue?: string;
  searchTerm?: string;
  onSearchChange: any;
  searchPlaceholder?: string;
  filters?: any[];
  filterFields?: any[];
}

function FilterBar({ 
  searchValue = '', 
  searchTerm,
  onSearchChange, 
  searchPlaceholder = 'Search...',
  filters = [],
  filterFields = [],
}: FilterBarProps) {
  const actualSearchValue = searchTerm !== undefined ? searchTerm : searchValue;
  const actualFilters = filterFields.length > 0 ? filterFields : filters;
  return (
    <Box 
      sx={{ 
        mb: 1.5, 
        p: 1.2, 
        backgroundColor: 'white',
        borderRadius: 1.5,
        border: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Grid container spacing={1.2} sx={{ 
        flexWrap: 'nowrap', 
        overflowX: 'auto',
        '&::-webkit-scrollbar': {
          display: 'none',
        },
        scrollbarWidth: 'none',
        msOverflowStyle: 'none',
      }}>
        {[{ isSearch: true }, ...actualFilters].map((filter, index) => (
          <Grid item key={index} sx={{ minWidth: 150, flex: '0 0 auto' }}>
            {filter.isSearch ? (
              <TextField
                placeholder={searchPlaceholder}
                value={actualSearchValue}
                onChange={(e) => onSearchChange(e.target.value)}
                fullWidth
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: 'text.secondary', fontSize: '0.95rem' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  minWidth: 120,
                  maxWidth: 320,
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'background.default',
                    height: 30,
                    fontSize: '0.75rem',
                  },
                }}
              />
            ) : (
              <TextField
                select={filter.type !== 'date'}
                label={filter.label}
                value={filter.value}
                onChange={(e) => filter.onChange(e.target.value)}
                fullWidth
                size="small"
                type={filter.type || 'text'}
                InputLabelProps={{
                  shrink: true,
                  sx: {
                    fontSize: '0.7rem',
                    transform: 'translate(14px, -6px) scale(1)',
                    backgroundColor: 'white',
                    px: 0.5,
                  },
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    fontSize: '0.75rem',
                    height: 30,
                  },
                }}
              >
                {filter.options?.map((option) => (
                  <MenuItem 
                    key={option.value} 
                    value={option.value}
                    sx={{
                      fontSize: '0.75rem',
                      minHeight: '32px',
                      py: 0.5,
                      px: 1.5,
                    }}
                  >
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            )}
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}

export default FilterBar;
